/**
 * AZ ÖSSZEFŰZŐ ÁTTEKINTŐ — több galériakép, melyik melyik számla oldala (#50).
 *
 * A mérce: `docs/tervezes/design/kliens-uj-kinezet/galeria-oldalak.html`, az
 * 1. út (Viktor, 2026-11-04). A kamera áttekintőjének (`ReviewSheet`) testvére,
 * ugyanazon a sötét rétegen és ugyanazokkal a sor-elemekkel — az ügyfél egy
 * nézetet tanul meg, nem kettőt.
 *
 * ═══ MI VÁLTOZIK A KAMERÁHOZ KÉPEST ═══
 *
 * A kamera sorozata EGY bizonylat. A galériából viszont jöhet öt számla tíz
 * képen: ezért itt a képek CSOPORTOKBAN állnak, és két csoport közt egy
 * „Összefűzöm" gomb van. A szabályok tiszta függvényekben élnek
 * (`grouping.ts`), ez a fájl csak megjeleníti őket.
 *
 * ═══ A KIINDULÁS KÉT IRÁNYBÓL ═══
 *
 * A „Feltöltök valamit" útról MINDEN KÉP KÜLÖN indul — ez a mai viselkedés,
 * és aki nem nyúl semmihez, ugyanazt kapja, amit eddig. A kérés kártyájáról
 * MIND EGYBEN: a kártya egy megnevezett számláról szól, a több kép ott szinte
 * biztosan annak az oldala (`together`).
 *
 * ═══ RÉTEG, NEM OLDAL ═══
 *
 * A feltöltés MŰVELET (`CLAUDE.md` §2) — ez a második fele, ugyanúgy, mint a
 * kamera áttekintője.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_FILES_PER_UPLOAD } from '@/contracts/generated/upload-limits';
import { PagePreview } from '@/features/capture/PagePreview';
import { fitsInOneRequest, type SessionLimit } from '@/features/capture/session-limits';
import {
  type Grouping,
  groupsOf,
  moveItem,
  removeItem,
  splitGroup,
  startGrouping,
  toggleJoin,
} from './grouping';

interface Picked {
  id: string;
  file: File;
}

interface GroupSheetProps {
  files: readonly File[];
  /** `true` = mind egy számla oldalaként indul (a kérés kártyája). */
  together: boolean;
  onCancel: () => void;
  /** A csoportok, a látható sorrendben — mindegyik EGY bizonylat. */
  onSend: (groups: File[][]) => void;
}

/**
 * A CSOPORT KORLÁTJÁNAK MONDATA — a gomb fölött, nem a gombon: a letiltott
 * gomb azt mondja, hogy nem megy, ez azt, hogy MIT csináljon.
 */
function LimitNote({ limit }: { limit: SessionLimit }) {
  const { t } = useTranslation();
  if (limit === 'ok') {
    return null;
  }
  return (
    <p className="review-limit" role="alert">
      {limit === 'too-many-pages'
        ? t('capture.review.tooManyPages', { count: MAX_FILES_PER_UPLOAD })
        : t('capture.review.tooLarge')}
    </p>
  );
}

/** Egy kép sora — a kamera áttekintőjének sora (`.review-page`), vágás nélkül. */
function PageRow({
  url,
  index,
  label,
  last,
  onOpen,
  onChange,
}: {
  /** A bélyegkép címe; amíg nincs kész, a gomb üres. */
  url: string | undefined;
  /** A kép helye a teljes sorrendben (0-tól). */
  index: number;
  label: string;
  last: boolean;
  onOpen: (button: HTMLButtonElement) => void;
  onChange: (update: (current: Grouping<Picked>) => Grouping<Picked>) => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="review-page">
      <button
        type="button"
        className="review-thumb"
        aria-label={t('grouping.open', { number: index + 1 })}
        onClick={(event) => onOpen(event.currentTarget)}
      >
        {url ? <img src={url} alt="" /> : null}
      </button>

      <div className="review-page-body">
        <p className="review-page-number">{label}</p>
        <div className="review-page-actions">
          <button type="button" onClick={() => onChange((current) => removeItem(current, index))}>
            {t('capture.review.remove')}
          </button>
        </div>
      </div>

      <div className="review-move">
        <button
          type="button"
          aria-label={t('capture.review.moveUp')}
          disabled={index === 0}
          onClick={() => onChange((current) => moveItem(current, index, -1))}
        >
          ↑
        </button>
        <button
          type="button"
          aria-label={t('capture.review.moveDown')}
          disabled={last}
          onClick={() => onChange((current) => moveItem(current, index, 1))}
        >
          ↓
        </button>
      </div>
    </li>
  );
}

/**
 * A BÉLYEGKÉPEK OBJEKTUM-CÍMEI — mountonként készülnek és szabadulnak fel.
 * Tíz telefonos fotó címe különben a lap élete végéig a memóriában maradna.
 */
function useObjectUrls(items: readonly Picked[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    const made: Record<string, string> = {};
    for (const item of items) {
      made[item.id] = URL.createObjectURL(item.file);
    }
    setUrls(made);
    return () => {
      for (const url of Object.values(made)) {
        URL.revokeObjectURL(url);
      }
    };
  }, [items]);
  return urls;
}

export function GroupSheet({ files, together, onCancel, onSend }: GroupSheetProps) {
  const { t } = useTranslation();

  // A BÉLYEGKÉPEK CÍME EGYSZER KÉSZÜL, és a réteg bezárásakor felszabadul —
  // tíz telefonos fotó objektum-URL-je különben a lap élete végéig a
  // memóriában maradna.
  const picked = useMemo<Picked[]>(
    () => files.map((file, index) => ({ id: `${index}-${file.name}-${file.size}`, file })),
    [files]
  );
  // A CÍMEKET AZ EFFEKT KÉSZÍTI ÉS VONJA VISSZA, nem a `useMemo`. A
  // StrictMode fejlesztésben mount → cleanup → mount sorrendben futtatja az
  // effektet, a memoizált értéket viszont megtartja: a memóban készült
  // címeket az első cleanup visszavonná, és a második mount törött képeket
  // mutatna. Így minden mount a sajátját készíti, és a sajátját szabadítja fel.
  const urls = useObjectUrls(picked);

  const [grouping, setGrouping] = useState<Grouping<Picked>>(() => startGrouping(picked, together));
  const groups = groupsOf(grouping);

  /** A teljes képernyőn megnyitott kép; `null` = a lista látszik. */
  const [openId, setOpenId] = useState<string | null>(null);
  const open = grouping.items.find((item) => item.id === openId) ?? null;
  const opener = useRef<HTMLButtonElement | null>(null);
  const closePreview = useCallback(() => {
    setOpenId(null);
    requestAnimationFrame(() => opener.current?.focus());
  }, []);

  // A KORLÁT CSOPORTONKÉNT: egy többképes számla EGY kérésben megy
  // (`useUploader.sendGroups`), tehát ugyanaz a 20 fájl / 25 MiB vonatkozik
  // rá, mint a kamera sorozatára. Az egyképesek darabolva mennek, nekik nincs.
  const limits = groups.map((group) =>
    group.length > 1 ? fitsInOneRequest(group.map((item) => item.file)) : ('ok' as const)
  );
  const sendable = groups.length > 0 && limits.every((limit) => limit === 'ok');

  // Az első kép sorszáma csoportonként — a képek folyamatosan számozódnak.
  let flatIndex = 0;

  return (
    <div className="review" role="dialog" aria-modal="true" aria-label={t('grouping.label')}>
      <header className="review-head">
        <h2>{t('grouping.title', { images: grouping.items.length, count: groups.length })}</h2>
        <p className="meta">{t(together ? 'grouping.hintTogether' : 'grouping.hint')}</p>
      </header>

      <div className="review-list">
        {grouping.items.length === 0 ? (
          <p className="review-empty">{t('grouping.empty')}</p>
        ) : (
          <ol className="group-list">
            {groups.map((group, groupIndex) => {
              const first = flatIndex;
              flatIndex += group.length;
              const limit = limits[groupIndex];
              return (
                // A KULCS A HELY (az első kép sorszáma), NEM AZ ELSŐ KÉP: ha a csoport első képe
                // mozdul, egy képhez kötött kulcs az egész csoportot
                // újramountolná, és a megnyomott nyíl fókusza elveszne.
                // A csoportnak nincs saját állapota; a sorokat a `PageRow`
                // `item.id` kulcsa rendezi át.
                <li key={`hely-${first}`} className="group-item">
                  {groupIndex > 0 ? (
                    <div className="group-joint">
                      <button
                        type="button"
                        aria-label={t('grouping.joinLabel', { a: first, b: first + 1 })}
                        onClick={() => setGrouping((current) => toggleJoin(current, first - 1))}
                      >
                        {t('grouping.join')}
                      </button>
                    </div>
                  ) : null}

                  <section
                    className={group.length > 1 ? 'group-block group-block-multi' : 'group-block'}
                    aria-label={t('grouping.invoice', {
                      number: groupIndex + 1,
                      count: group.length,
                    })}
                  >
                    {group.length > 1 ? (
                      <div className="group-head">
                        <strong>
                          {t('grouping.invoice', { number: groupIndex + 1, count: group.length })}
                        </strong>
                        <button
                          type="button"
                          onClick={() => setGrouping((current) => splitGroup(current, groupIndex))}
                        >
                          {t('grouping.split')}
                        </button>
                      </div>
                    ) : null}

                    <ol className="group-pages">
                      {group.map((item, inGroup) => (
                        <PageRow
                          key={item.id}
                          url={urls[item.id]}
                          index={first + inGroup}
                          // A TÖBBOLDALAS SZÁMLÁBAN OLDAL, különben KÉP: az
                          // ügyfél így látja, mit fűzött már össze.
                          label={
                            group.length > 1
                              ? t('capture.pageNumber', { number: inGroup + 1 })
                              : t('grouping.image', { number: first + inGroup + 1 })
                          }
                          last={first + inGroup === grouping.items.length - 1}
                          onOpen={(button) => {
                            opener.current = button;
                            setOpenId(item.id);
                          }}
                          onChange={setGrouping}
                        />
                      ))}
                    </ol>

                    <LimitNote limit={limit} />
                  </section>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <footer className="review-foot">
        <div className="review-foot-actions">
          <button type="button" className="pick pick-link" onClick={onCancel}>
            {t('grouping.cancel')}
          </button>
          <button
            type="button"
            className="pick pick-primary"
            disabled={!sendable}
            onClick={() => onSend(groups.map((group) => group.map((item) => item.file)))}
          >
            {t('grouping.send', { count: groups.length })}
          </button>
        </div>
      </footer>

      {open === null ? null : (
        <PagePreview
          src={urls[open.id] ?? ''}
          title={t('grouping.image', { number: grouping.items.indexOf(open) + 1 })}
          onClose={closePreview}
        />
      )}
    </div>
  );
}
